import { useMutation } from '@apollo/client/react'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { transitionDuration } from '@faceup/ui'
import { Flex, Typography, useMessage, useUiBaseConfig } from '@faceup/ui-base'
import { CASE_RATING_RANGE, type CaseRating, CaseStatusGroup, RATING_EMOJIS } from '@faceup/utils'
import moment from 'moment-timezone'
import { useEffect, useRef, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { NotRedactableWrapper } from '../Redaction'

const mutations = {
  UpdateReportRating: graphql(`
    mutation UpdateReportRating($input: UpdateReportRatingInput!) {
      updateReportRating(input: $input) {
        case {
          id
          rating
          ratedAt
        }
      }
    }
  `),
}

const fragments = {
  ReportRating_case: graphql(`
    fragment ReportRating_case on CaseInterface {
      id
      status {
        id
        groupName
      }
      victim {
        id
      }
      rating
      ratedAt
    }
  `),
}

const messages = defineMessages('report.components.chat.reportRating', [
  { id: 'ratingTitle', defaultMessage: 'How satisfied are you with the solution?' },
  { id: 'ratingSaved', defaultMessage: 'Your rating was saved' },
  { id: 'victimHasRated', defaultMessage: 'Sender rated the solution as ' },
  { id: 'iHaveRated', defaultMessage: 'You rated the solution as ' },
] as const)

const ratingMessages = defineMessages('report.components.chat.reportRating.ratingMessages', [
  { id: '1', defaultMessage: 'Horrible' },
  { id: '2', defaultMessage: 'Bad' },
  { id: '3', defaultMessage: 'OK' },
  { id: '4', defaultMessage: 'Good' },
  { id: '5', defaultMessage: 'Great' },
] as const)

type ReportRatingProps = {
  report: FragmentType<typeof fragments.ReportRating_case>
  userId: string
  onRatingChange?: (rating: CaseRating) => void
}

export const ReportRating = ({ report: _report, userId, onRatingChange }: ReportRatingProps) => {
  const report = getFragmentData(fragments.ReportRating_case, _report)
  const { token } = useUiBaseConfig()
  const { formatMessage } = useIntl()
  const message = useMessage()

  const [updateRating, { loading }] = useMutation(mutations.UpdateReportRating, {
    onCompleted: () => {
      message.success(formatMessage(messages.ratingSaved))
    },
  })

  const isUserVictim = Boolean(report?.victim?.id) && userId === report?.victim?.id
  const hasRating = Boolean(report?.rating)
  const reportRating = report?.rating
  const isReportClosed = report?.status?.groupName === CaseStatusGroup.Closed

  const textRefs = useRef<{ [key in CaseRating]?: HTMLSpanElement | null }>({})
  const [maxWidth, setMaxWidth] = useState<number>(0)

  useEffect(() => {
    if (textRefs.current) {
      const widths = Object.values(textRefs.current).map(
        ref => ref?.getBoundingClientRect().width || 0
      )
      setMaxWidth(Math.max(...widths) + 4)
    }
  }, [])

  if (!isReportClosed) {
    return null
  }

  if (isUserVictim && !hasRating) {
    return (
      <div
        style={{
          transition: `opacity ${transitionDuration} ease`,
          fontSize: '28px',
        }}
      >
        <Flex
          vertical
          justify='center'
          align='center'
          gap='24px'
          style={{
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: '24px',
          }}
        >
          <Typography.Text strong>
            <FormattedMessage message={messages.ratingTitle} />
          </Typography.Text>
          <Flex justify='center' align='center'>
            {CASE_RATING_RANGE.map(rating => (
              <Flex
                key={rating}
                vertical
                justify='center'
                align='center'
                className='child-hover'
                style={{
                  width: maxWidth ? `${maxWidth}px` : 'auto',
                  minWidth: '64px',
                  borderRight: `1px ${token.colorBorderSecondary}`,
                  cursor: 'pointer',
                  ...(loading && { pointerEvents: 'none', opacity: 0.5, cursor: undefined }),
                }}
                onClick={() => {
                  updateRating({
                    variables: {
                      input: {
                        rating,
                      },
                    },
                  })
                  onRatingChange?.(rating)
                }}
              >
                <span className='hover-target' style={{ fontSize: '28px' }}>
                  {RATING_EMOJIS[rating]}
                </span>
                <span
                  ref={el => {
                    textRefs.current[rating] = el
                  }}
                >
                  <Typography.Text>{formatMessage(ratingMessages[rating])}</Typography.Text>
                </span>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </div>
    )
  }

  if (reportRating) {
    return (
      <NotRedactableWrapper>
        <Flex align='center' justify='center' gap='3px' vertical>
          <div>
            <span style={{ fontSize: '20px', marginRight: '5px' }}>
              {RATING_EMOJIS[reportRating]}
            </span>
            <span>
              {isUserVictim ? (
                <FormattedMessage message={messages.iHaveRated} />
              ) : (
                <FormattedMessage message={messages.victimHasRated} />
              )}
            </span>
            {'  '}
            <span style={{ fontWeight: 'bold' }}>
              {formatMessage(ratingMessages[reportRating])}
            </span>
          </div>

          <Typography.Text type='secondary' size='sm'>
            {moment(report.ratedAt).format('LLL')}
          </Typography.Text>
        </Flex>
      </NotRedactableWrapper>
    )
  }

  return null
}
